import { ActionCreator } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { UserSettingsApi } from '../../../api/userSettingsApi'
import { toastMsg } from '../../../components/Toast/toastMsg'
import { IPagination } from '../../../types/pagination'
import { ITotpConfig } from '../../../types/user'
import { UserSettingsState } from '../reducers'
import { IEmployee, IInvolvementEmployees } from '../types/employee'
import * as constants from './constants'

export type EmployeeThunkResult<R> = ThunkAction<Promise<R>, UserSettingsState, null, EmployeeActions>
export type EmployeeThunkDispatchType = ThunkDispatch<UserSettingsState, null, EmployeeActions>

export interface EmployeesHasErrored {
  type: constants.EMPLOYEES_HAS_ERRORED
  hasErrored: boolean
}

export interface GetPaginatedEmployeesSuccess {
  type: constants.GET_PAGINATED_EMPLOYEES_SUCCESS
  employees: IPagination<IEmployee[]>
}

export interface GetEmployeesSuccess {
  type: constants.GET_EMPLOYEES_SUCCESS
  employees: IEmployee[]
}

export interface GetEmployeeSuccess {
  type: constants.GET_EMPLOYEE_SUCCESS
  employee: IEmployee
}

export interface DeleteEmployeeSuccess {
  type: constants.DELETE_EMPLOYEE_SUCCESS
  employee: IEmployee
}

export interface CreateEmployeeSuccess {
  type: constants.CREATE_EMPLOYEE_SUCCESS
  employee: IEmployee,
  idx: number
}

export interface UpdateEmployeeSuccess {
  type: constants.UPDATE_EMPLOYEE_SUCCESS
  employee: IEmployee
}

export interface UpdateEmployeePhotoSuccess {
  type: constants.UPDATE_EMPLOYEE_PHOTO_SUCCESS
  employee: IEmployee
}

export interface RemoveEmployeePhotoSuccess {
  type: constants.REMOVE_EMPLOYEE_PHOTO_SUCCESS
  id: string | number
}

export interface SortEmployees {
  type: constants.SORT_EMPLOYEES,
  sort: { name: string, direction: string }
}

export interface FilterEmployees {
  type: constants.FILTER_EMPLOYEES,
  search: string
}

export interface SelectEmployee {
  type: constants.SELECT_EMPLOYEE,
  employee: IEmployee
}

export interface OpenEmployeeForm {
  type: constants.OPEN_EMPLOYEE_FORM,
  open: boolean
}

export interface SetEmployeesPositionsSuccess {
  type: constants.SET_EMPLOYEES_POSITIONS_SUCCESS
  employees: IEmployee[]
}

export interface SetEmployeeActivePage {
  type: constants.SET_EMPLOYEE_ACTIVE_PAGE
  page: number
}

export interface GetOpenProjectEmployeesSuccess {
  type: constants.GET_EMPLOYEES_OPEN_PROJECT_SUCCESS
  employees: IEmployee[]
}

export interface GetPaginatedOpenProjectEmployeesSuccess {
  type: constants.GET_PAGINATED_EMPLOYEES_OPEN_PROJECT_SUCCESS
  employees: IPagination<IEmployee[]>
}

export interface SetPaginatedOpenProjectEmployeesActivePage {
  type: constants.SET_PAGINATED_EMPLOYEES_OPEN_PROJECT_ACTIVE_PAGE
  page: number
}

export interface GetInvolvementEmployeesSuccess {
  type: constants.GET_INVOLVEMENT_EMPLOYEES_SUCCESS
  involvementEmployees: IInvolvementEmployees[]
}

export interface GetPaginatedInvolvementEmployeesSuccess {
  type: constants.GET_PAGINATED_INVOLVEMENT_EMPLOYEES_SUCCESS
  employees: IPagination<IInvolvementEmployees[]>
}

export interface SetPaginatedInvolvementEmployeesActivePage {
  type: constants.SET_PAGINATED_INVOLVEMENT_EMPLOYEES_ACTIVE_PAGE
  page: number
}

export interface SetPasswordSuccess {
  type: constants.SET_PASSWORD_SUCCESS
}

export interface GetTOTPConfSuccess {
  type: constants.GET_TOT_CONF_SUCCESS
  totp: ITotpConfig
}

export interface SetEmailNotificationsSuccess {
  type: constants.UPDATE_EMPLOYEE_SUCCESS,
  employee: IEmployee
}

export const setEmailNotifications: ActionCreator<EmployeeThunkResult<IEmployee>>
  = (id: number, emailNotifications: boolean): EmployeeThunkResult<IEmployee> => {
    return async (dispatch: EmployeeThunkDispatchType) => {
      return UserSettingsApi.setEmailNotifications(id, emailNotifications)
        .then(response => {
          dispatch(setEmailNotificationsSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(employeesHasErrored(true, error.response.data, constants.SET_EMAIL_NOTIFICATIONS))
          return {} as IEmployee
        })
  };
}

export const setEmailNotificationsSuccess = (employee: IEmployee): SetEmailNotificationsSuccess => {
  toastMsg(`Notifications setting updated.`, constants.UPDATE_EMPLOYEE_SUCCESS, 'success');
  return {
    type: constants.UPDATE_EMPLOYEE_SUCCESS,
    employee
  };
}

export const employeesHasErrored = (hasErrored: boolean, error: Error, action?: string): EmployeesHasErrored => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.EMPLOYEES_HAS_ERRORED,
    hasErrored: hasErrored
  }
}

export const getEmployee: ActionCreator<EmployeeThunkResult<IEmployee>>
  = (id: string | number): EmployeeThunkResult<IEmployee> => {
    return async (dispatch: EmployeeThunkDispatchType) => {
      return UserSettingsApi.getEmployee(id)
        .then(response => {
          dispatch(getEmployeeSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(employeesHasErrored(true, error.response.data, constants.GET_EMPLOYEE))
          return {} as IEmployee
        })
    }
  }

export const getEmployeeSuccess = (employee: IEmployee): GetEmployeeSuccess => {
  return {
    type: constants.GET_EMPLOYEE_SUCCESS,
    employee
  }
}

export const getOpenProjectEmployees: ActionCreator<EmployeeThunkResult<IEmployee[]>>
  = (id: string | number): EmployeeThunkResult<IEmployee[]> => {
    return async (dispatch: EmployeeThunkDispatchType) => {
      return UserSettingsApi.getOpenProjectEmployees(id)
        .then(response => {
          dispatch(getOpenProjectEmployeesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(employeesHasErrored(true, error.response.data, constants.GET_EMPLOYEES_OPEN_PROJECT))
          return [] as IEmployee[]
        })
    }
  }

export const getOpenProjectEmployeesSuccess = (employees: IEmployee[]): GetOpenProjectEmployeesSuccess => {
  return {
    type: constants.GET_EMPLOYEES_OPEN_PROJECT_SUCCESS,
    employees
  }
}

export const getPaginatedOpenProjectEmployees: ActionCreator<EmployeeThunkResult<IPagination<IEmployee[]>>>
= (id: string | number, queryParams?: Record<string, string> ): EmployeeThunkResult<IPagination<IEmployee[]>> => {
  return async (dispatch: EmployeeThunkDispatchType) => {
    return UserSettingsApi.getPaginatedOpenProjectEmployees(id, queryParams)
    .then(response => {
      dispatch(getPaginatedOpenProjectEmployeesSuccess(response))
      return response
    })
    .catch(error => {
      dispatch(employeesHasErrored(true, error.response.data, constants.GET_PAGINATED_EMPLOYEES_OPEN_PROJECT))
      return {} as IPagination<IEmployee[]>
    })
  }
}

export const getPaginatedOpenProjectEmployeesSuccess = (employees: IPagination<IEmployee[]>): GetPaginatedOpenProjectEmployeesSuccess => {
  return {
    type: constants.GET_PAGINATED_EMPLOYEES_OPEN_PROJECT_SUCCESS,
    employees
  }
}

export const setPaginatedOpenProjectEmployeesActivePage = (page: number): SetPaginatedOpenProjectEmployeesActivePage => {
  return {
    type: constants.SET_PAGINATED_EMPLOYEES_OPEN_PROJECT_ACTIVE_PAGE,
    page: page
  }
}

export const getInvolvementEmployees: ActionCreator<EmployeeThunkResult<IInvolvementEmployees[]>>
  = (id: string | number, params: Record<string, string>): EmployeeThunkResult<IInvolvementEmployees[]> => {
    return async (dispatch: EmployeeThunkDispatchType) => {
      return UserSettingsApi.getInvolevedEmployees(id, params)
        .then(response => {
          dispatch(getInvolvementEmployeesSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(employeesHasErrored(true, error.response.data, constants.GET_INVOLVEMENT_EMPLOYEES))
          return [] as IInvolvementEmployees[]
        })
    }
  }

export const getInvolvementEmployeesSuccess = (involvementEmployees: IInvolvementEmployees[]): GetInvolvementEmployeesSuccess => {
  return {
    type: constants.GET_INVOLVEMENT_EMPLOYEES_SUCCESS,
    involvementEmployees
  }
}

export const getPaginatedInvolvementEmployees: ActionCreator<EmployeeThunkResult<IPagination<IInvolvementEmployees[]>>>
= (id: string | number, queryParams: Record<string, string> ): EmployeeThunkResult<IPagination<IInvolvementEmployees[]>> => {
  return async (dispatch: EmployeeThunkDispatchType) => {
    return UserSettingsApi.getPaginatedInvolevedEmployees(id, queryParams)
    .then(response => {
      dispatch(getPaginatedInvolvementEmployeesSuccess(response))
      return response
    })
    .catch(error => {
      dispatch(employeesHasErrored(true, error.response.data, constants.GET_PAGINATED_INVOLVEMENT_EMPLOYEES))
      return {} as IPagination<IInvolvementEmployees[]>
    })
  }
}

export const getPaginatedInvolvementEmployeesSuccess = (employees: IPagination<IInvolvementEmployees[]>): GetPaginatedInvolvementEmployeesSuccess => {
  return {
    type: constants.GET_PAGINATED_INVOLVEMENT_EMPLOYEES_SUCCESS,
    employees
  }
}

export const setPaginatedInvolvementEmployeesActivePage = (page: number): SetPaginatedInvolvementEmployeesActivePage => {
  return {
    type: constants.SET_PAGINATED_INVOLVEMENT_EMPLOYEES_ACTIVE_PAGE,
    page: page
  }
}

export const getEmployees: ActionCreator<EmployeeThunkResult<IEmployee[]>>
= (queryParams?: Record<string, string>): EmployeeThunkResult<IEmployee[]> => {
  return async (dispatch: EmployeeThunkDispatchType) => {
    return UserSettingsApi.getEmployees(queryParams)
    .then(response => {
      dispatch(getEmployeesSuccess(response))
      return response
    })
    .catch(error => {
      dispatch(employeesHasErrored(true, error.response.data, constants.GET_EMPLOYEES))
      return []
    })
  }
}

export const getEmployeesSuccess = (employees: IEmployee[]): GetEmployeesSuccess => {
  return {
    type: constants.GET_EMPLOYEES_SUCCESS,
    employees
  }
}

export const getPaginatedEmployees: ActionCreator<EmployeeThunkResult<IPagination<IEmployee[]>>>
= (queryParams?: Record<string, string> ): EmployeeThunkResult<IPagination<IEmployee[]>> => {
  return async (dispatch: EmployeeThunkDispatchType) => {
    return UserSettingsApi.getPaginatedEmployees(queryParams)
    .then(response => {
      dispatch(getPaginatedEmployeesSuccess(response))
      return response
    })
    .catch(error => {
      dispatch(employeesHasErrored(true, error.response.data, constants.GET_PAGINATED_EMPLOYEES))
      return {} as IPagination<IEmployee[]>
    })
  }
}

export const getPaginatedEmployeesSuccess = (employees: IPagination<IEmployee[]>): GetPaginatedEmployeesSuccess => {
  return {
    type: constants.GET_PAGINATED_EMPLOYEES_SUCCESS,
    employees
  }
}

export const deleteEmployee: ActionCreator<EmployeeThunkResult<string | number | void>>
 = (employee: IEmployee): EmployeeThunkResult<string | number | void> => {
  return async (dispatch: EmployeeThunkDispatchType) => {
    return UserSettingsApi.deleteEmployee(employee.id)
      .then(response => {
        dispatch(deleteEmployeeSuccess(employee))
        return response
      })
      .catch(error => {
        dispatch(employeesHasErrored(true, error.response.data, constants.DELETE_EMPLOYEE))

      })
  }
}

export const deleteEmployeeSuccess = (employee: IEmployee): DeleteEmployeeSuccess => {
  toastMsg(`Deleted ${employee.full_name} employee successfully.`, constants.DELETE_EMPLOYEE_SUCCESS, 'success')
  return {
    type: constants.DELETE_EMPLOYEE_SUCCESS,
    employee
  }
}

export const createEmployee: ActionCreator<EmployeeThunkResult<IEmployee>>
= (employee: IEmployee, idx: number): EmployeeThunkResult<IEmployee> => {
  return async (dispatch: EmployeeThunkDispatchType) => {
    return UserSettingsApi.createEmployee(employee)
      .then(response => {
        dispatch(createEmployeeSuccess(response, idx))
        return response
      })
      .catch(error => {
        dispatch(employeesHasErrored(true, error.response.data, constants.CREATE_EMPLOYEE))
        return {} as IEmployee
      })
  }
}

export const createEmployeeSuccess = (employee: IEmployee, idx: number): CreateEmployeeSuccess => {
  toastMsg(`Created ${employee.full_name} employee successfully.`, constants.CREATE_EMPLOYEE_SUCCESS, 'success')
  return {
    type: constants.CREATE_EMPLOYEE_SUCCESS,
    employee,
    idx
  }
}

export const updateEmployee: ActionCreator<EmployeeThunkResult<IEmployee>>
 = (employee: IEmployee): EmployeeThunkResult<IEmployee>=> {
  return async (dispatch: EmployeeThunkDispatchType) => {
    return UserSettingsApi.updateEmployee(employee)
      .then(response => {
        dispatch(updateEmployeeSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(employeesHasErrored(true, error.response.data, constants.UPDATE_EMPLOYEE))
        return {} as IEmployee
      })
  }
}

export const updateEmployeeSuccess = (employee: IEmployee): UpdateEmployeeSuccess => {
  toastMsg(`Updated ${employee.full_name} employee successfully. To apply these changes a refresh of the website is required.`, constants.UPDATE_EMPLOYEE_SUCCESS, 'success')
  return {
    type: constants.UPDATE_EMPLOYEE_SUCCESS,
    employee
  }
}

export const updateEmployeePhoto: ActionCreator<EmployeeThunkResult<IEmployee>>
  = (employee: IEmployee, file?: File): EmployeeThunkResult<IEmployee> => {
    return async (dispatch: EmployeeThunkDispatchType) => {
      if (file && file.size > 2 * 1024 * 1024) {
        const _error: Error = { name: '', message: 'Failed to upload the photo - file size Exceeds 2MB limit.' }
        dispatch(employeesHasErrored(true, _error, constants.UPDATE_EMPLOYEE_PHOTO))
        return {} as IEmployee
      } else {
        return UserSettingsApi.updateEmployeePhoto(employee, file)
          .then(response => {
            dispatch(updateEmployeePhotoSuccess(response))
            return response
          })
          .catch(error => {
            const _error: Error = { name: error.response.data.name, message: `Failed to update ${employee.full_name} employee photo. ${error.response.data.message}` }
            dispatch(employeesHasErrored(true, _error, constants.UPDATE_EMPLOYEE_PHOTO))
            return {} as IEmployee
          })
      }
    }
  }

export const updateEmployeePhotoSuccess = (employee: IEmployee): UpdateEmployeePhotoSuccess => {
  toastMsg(`Updated ${employee.full_name} employee photo successfully.`, constants.UPDATE_EMPLOYEE_PHOTO_SUCCESS, 'success')
  return {
    type: constants.UPDATE_EMPLOYEE_PHOTO_SUCCESS,
    employee
  }
}

export const removeEmployeePhoto: ActionCreator<EmployeeThunkResult<string | number>>
 = (id: string | number): EmployeeThunkResult<string | number>=> {
  return async (dispatch: EmployeeThunkDispatchType) => {
    return UserSettingsApi.removeEmployeePhoto(id)
      .then(response => {
        dispatch(removeEmployeePhotoSuccess(response))
        return response
      })
      .catch(error => {
        const _error: Error = { name: error.response.data.name,  message: `Failed to remove employee photo. ${error.response.data.message}` }
        dispatch(employeesHasErrored(true, _error, constants.REMOVE_EMPLOYEE_PHOTO))
        return ''
      })
  }
}

export const removeEmployeePhotoSuccess = (id: string | number): RemoveEmployeePhotoSuccess => {
  toastMsg(`Removed employee photo successfully.`, constants.REMOVE_EMPLOYEE_PHOTO_SUCCESS, 'success')
  return {
    type: constants.REMOVE_EMPLOYEE_PHOTO_SUCCESS,
    id
  }
}

export const sortEmployees = (name: string, direction: string): SortEmployees => {
  return {
    type: constants.SORT_EMPLOYEES,
    sort: { name: name, direction: direction }
  }
}

export const filterEmployees = (search: string): FilterEmployees => {
  return {
    type: constants.FILTER_EMPLOYEES,
    search: search
  }
}

export const selectEmployee = (employee: IEmployee): SelectEmployee => {
  return {
    type: constants.SELECT_EMPLOYEE,
    employee: employee
  }
}

export const openEmployeeForm = (open: boolean): OpenEmployeeForm => {
  return {
    type: constants.OPEN_EMPLOYEE_FORM,
    open: open
  }
}

export const setEmployeeActivePage = (page: number): SetEmployeeActivePage => {
  return {
    type: constants.SET_EMPLOYEE_ACTIVE_PAGE,
    page: page
  }
}

export const setEmployeesPositions: ActionCreator<EmployeeThunkResult<IEmployee[]>>
 = (employees: IEmployee[]): EmployeeThunkResult<IEmployee[]>=> {
  return async (dispatch: EmployeeThunkDispatchType) => {
    return UserSettingsApi.setEmployeesPositions(employees)
      .then(response => {
        dispatch(setEmployeesPositionsSuccess(response))
        return response
      })
      .catch(error => {
        dispatch(employeesHasErrored(true, error.response.data, constants.SET_EMPLOYEES_POSITIONS))
        return []
      })
  }
}

export const setEmployeesPositionsSuccess = (employees: IEmployee[]): SetEmployeesPositionsSuccess => {
  toastMsg(`Updated employees successfully.`, constants.UPDATE_EMPLOYEE_SUCCESS, 'success')
  return {
    type: constants.SET_EMPLOYEES_POSITIONS_SUCCESS,
    employees
  }
}

export const setPassword: ActionCreator<EmployeeThunkResult<null>>
  = (id: number, pwd: string, old_pwd: string): EmployeeThunkResult<null> => {
  return async (dispatch: EmployeeThunkDispatchType) => {
    return UserSettingsApi.setPassword(id, pwd, old_pwd)
      .then(() => {
        dispatch(setPasswordSuccess())
        return null
      })
      .catch(error => {
        dispatch(employeesHasErrored(true, error.response.data, constants.SET_PASSWORD))
        return null
      })
  };
}

export const setPasswordSuccess = (): SetPasswordSuccess => {
  toastMsg(`Password changed.`, constants.SET_PASSWORD_SUCCESS, 'success');
  return {
    type: constants.SET_PASSWORD_SUCCESS
  };
}


export type EmployeeActions
  = GetEmployeesSuccess
  | DeleteEmployeeSuccess
  | CreateEmployeeSuccess
  | UpdateEmployeeSuccess
  | EmployeesHasErrored
  | SortEmployees
  | FilterEmployees
  | GetPaginatedEmployeesSuccess
  | SelectEmployee
  | OpenEmployeeForm
  | SetEmployeesPositionsSuccess
  | SetEmployeeActivePage
  | GetEmployeeSuccess
  | GetOpenProjectEmployeesSuccess
  | GetInvolvementEmployeesSuccess
  | UpdateEmployeePhotoSuccess
  | SetEmailNotificationsSuccess
  | SetPasswordSuccess
  | RemoveEmployeePhotoSuccess
  | GetPaginatedOpenProjectEmployeesSuccess
  | SetPaginatedOpenProjectEmployeesActivePage
  | GetPaginatedInvolvementEmployeesSuccess
  | SetPaginatedInvolvementEmployeesActivePage
